
<template>
  <div class="main-content">
    <Container
      :title="t('route.material')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          ref="searchFormRef"
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcMaterialEdit'])"
          @click="handleCreate()"
          type="success"
        >
          {{ t('table.add') }}
        </el-button>
        <el-button
          v-if="selection.length > 0 && this.$hasPermission(['ACTION_vcMaterialEdit'])"
          @click="handleBathDelete()"
          type="danger"
        >
          {{ t('table.bathDelete') }}
        </el-button>
        <SimpleTable
          :isLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :sort="sort"
          :order="order"
          :operateColumnWidth="300"
          @handleOrder="handleOrder"
          @handleSelection="handleSelection"
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="handleExamine(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.examine') }}
            </el-button>
            <el-button
              v-if="this.$hasPermission(['ACTION_vcMaterialEdit'])"
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
            <el-button
              @click="handleTags(slotProps.row.id)"
              type="primary"
              size="mini"
            >
              {{ t('common.tags') }}
            </el-button>
            <el-button
              v-if="this.$hasPermission(['ACTION_vcMaterialEdit'])"
              @click="handleDelete(slotProps.row.id)"
              type="danger"
              size="mini"
            >
              {{ t('common.delete') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <el-dialog
      v-if="this.$hasPermission(['ACTION_vcMaterialEdit'])"
      :title="t(textMap[dialogStatus])"
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NomalContainer>
        <CenterForm
          v-if="dialogStatus === 'create'"
          :formModel="createModel"
          :formStruct="createStruct"
          :formRules="materialRole"
          @submit="createSubmit"
          @reset="createReset"
        >
          <template #default="">
            <UploadFormItem
              ref="createUpload"
              prop="labelPreviewImage"
              :label="t('material.labelPreviewImage')"
              @change="labelPreviewImageChange"
            />
            <UploadFormItem
              ref="labelBackgroundImageRef"
              prop="labelBackgroundImage"
              :label="t('material.labelBackgroundImage')"
              @change="labelBackgroundImageChange"
            />
          </template>
        </CenterForm>
        <CenterForm
          v-if="dialogStatus === 'update'"
          :formModel="updateModel"
          :formStruct="updateStruct"
          :formRules="materialRole"
          @submit="updateNameSubmit"
          @reset="updateNameReset"
        >
          <template #default="">
            <UploadFormItem
              ref="updateLabelPreviewImageRef"
              prop="labelPreviewImageUpdate"
              :label="t('material.labelPreviewImage')"
              @change="updateLabelPreviewImageChange"
            />
            <UploadFormItem
              ref="updateLabelBackgroundImageRef"
              prop="labelBackgroundImageUpdate"
              :label="t('material.labelBackgroundImage')"
              @change="updateLabelBackgroundImageChange"
            />
          </template>
        </CenterForm>
        <template v-if="dialogStatus === 'tags'">
          <el-tag
            v-for="tag in materialTags"
            :key="tag.id"
            class="mx-1"
            closable
            :disable-transitions="false"
            @close="deleteTag(tag.id)"
            style="margin-right: 10px;"
          >
            {{ tag.tag }}
          </el-tag>
          <el-select
            v-if="searchTagVisible"
            v-model="materialTagsValue"
            style="width: 240px; vertical-align: middle"
            allow-create
            filterable
            remote
            :remote-method="getAllTags"
            :loading="loadingTags"
            @change="selectTag"
          >
            <el-option
              v-for="item in allTags"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-button
            v-else
            class="button-new-tag ml-1"
            size="small"
            @click="showSearchTag"
          >
            + {{ t('common.addTags') }}
          </el-button>
        </template>
        <template v-if="dialogStatus === 'examine'">
          <FormView :data="examineModel" />
        </template>
      </NomalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import UploadFormItem from '@/components/form/common/UploadFormItem.vue'
import FormView from '@/components/form/view/FormView.vue'
import {
  MaterialModel,
  MaterialPageSearchModel,
  MaterialSearchModel,
  MaterialAddModel,
  MaterialUpdateModel,
  MaterialAddViewModel,
  MaterialUpdateViewModel,
  IdsModel,
  AddTagsModel,
  MaterialTagsModel
} from '@/model/product/MaterialModel'
import {
  pageSearchMaterial,
  addMaterial,
  updateMaterial,
  removeMaterial,
  batchRemoveMaterial,
  addMaterialTags,
  removeMaterialTags,
  getMaterialTags
} from '@/apis/product/material'
import {
  TagsSearchModel,
  TagsAddModel
} from '@/model/product/TagsModel'
import {
  addTags,
  getTagsByValue,
  searchTags
} from '@/apis/product/tags'
import { RibbonColorDefSearchModel } from '@/model/product/RibbonColorDefModel'
import { searchRibbonColorDef } from '@/apis/product/ribbonColorDef'
import { PaperTextureDefSearchModel } from '@/model/product/PaperTextureDefModel'
import { searchPaperTextureDef } from '@/apis/product/paperTextureDef'
import { PaperColorDefSearchModel } from '@/model/product/PaperColorDefModel'
import { searchPaperColorDef } from '@/apis/product/paperColorDef'
import { MaterialCategorySearchModel } from '@/model/product/MaterialCategoryModel'
import { searchMaterialCategory } from '@/apis/product/materialCategory'
import { MaterialTypeSearchModel } from '@/model/product/MaterialTypeModel'
import { searchMaterialType } from '@/apis/product/materialType'
// import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { selectOptionFormat } from '@/utils/options'
import { /* MaterialType, */prePrinting } from '@/constant/options'
import { useI18n } from 'vue-i18n'
// import { useStore } from '@/store'
// import networkConfig from '@/config/default/net.config'
// import { Route } from '@/constant/route'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer,
    UploadFormItem,
    FormView
  },
  setup() {
    const { t } = useI18n()
    const createUpload = ref()
    const labelBackgroundImageRef = ref()
    const updateLabelPreviewImageRef = ref()
    const updateLabelBackgroundImageRef = ref()
    const searchFormRef = ref(ElForm)
    // const materialTypeOption = selectOptionFormat(MaterialType.materialTypeOptions, t, MaterialType.i18nKey)
    const prePrintingOption = selectOptionFormat(prePrinting.prePrintingOptions, t, prePrinting.i18nKey)
    let ribbonColors: any[] = []
    let paperColors: any[] = []
    let paperTextures: any[] = []
    let materialTypes: any[] = []
    let materialCategorys: any[] = []
    const searchStruct = [
      { prop: 'materialModel', label: t('material.materialModel'), placeholder: t('material.materialModelP'), component: 'el-input', type: 'text', model: null },
      { prop: 'materialName', label: t('material.materialName'), placeholder: t('material.materialNameP'), component: 'el-input', type: 'text', model: null },
      {
        prop: 'materialType',
        label: t('material.materialType'),
        placeholder: t('material.materialTypeP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'labelWidth', label: t('material.labelWidth'), placeholder: t('material.labelWidthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'usableWidth', label: t('material.usableWidth'), placeholder: t('material.usableWidthP'), component: 'el-input', type: 'number', model: null },
      {
        prop: 'ribbonColor',
        label: t('material.ribbonColor'),
        placeholder: t('material.ribbonColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperColor',
        label: t('material.paperColor'),
        placeholder: t('material.paperColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperTexture',
        label: t('material.paperTexture'),
        placeholder: t('material.paperTextureP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'materialCategory',
        label: t('material.materialCategory'),
        placeholder: t('material.materialCategoryP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'rawMaterialNumber', label: t('material.rawMaterialNumber'), placeholder: t('material.rawMaterialNumberP'), component: 'el-input', type: 'text', model: null },
      { prop: 'texture', label: t('material.texture'), placeholder: t('material.textureP'), component: 'el-input', type: 'text', model: null },
      { prop: 'prePrinting', label: t('material.prePrinting'), placeholder: t('material.prePrintingP'), component: 'el-select', type: 'number', model: null, options: prePrintingOption },
      { prop: 'specialSize', label: t('material.specialSize'), placeholder: t('material.specialSizeP'), component: 'el-input', type: 'text', model: null },
      { prop: 'barcodeRibbon', label: t('material.barcodeRibbon'), placeholder: t('material.barcodeRibbonP'), component: 'el-input', type: 'text', model: null }
    ]

    const createStruct = [
      { prop: 'materialModel', label: t('material.materialModel'), placeholder: t('material.materialModelP'), component: 'el-input', type: 'text', model: null },
      { prop: 'materialName', label: t('material.materialName'), placeholder: t('material.materialNameP'), component: 'el-input', type: 'text', model: null },
      {
        prop: 'materialType',
        label: t('material.materialType'),
        placeholder: t('material.materialTypeP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'labelWidth', label: t('material.labelWidth'), placeholder: t('material.labelWidthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'labelLength', label: t('material.labelLength'), placeholder: t('material.labelLengthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'usableWidth', label: t('material.usableWidth'), placeholder: t('material.usableWidthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'usableLength', label: t('material.usableLength'), placeholder: t('material.usableLengthP'), component: 'el-input', type: 'number', model: null },
      {
        prop: 'ribbonColor',
        label: t('material.ribbonColor'),
        placeholder: t('material.ribbonColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperColor',
        label: t('material.paperColor'),
        placeholder: t('material.paperColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperTexture',
        label: t('material.paperTexture'),
        placeholder: t('material.paperTextureP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      // { prop: 'labelPreviewImage', label: t('material.labelPreviewImage'), placeholder: t('material.labelPreviewImageP'), component: 'el-input', type: 'text', model: null },
      { prop: 'labelCapacity', label: t('material.labelCapacity'), placeholder: t('material.labelCapacityP'), component: 'el-input', type: 'number', model: null },
      { prop: 'encapsulationType', label: t('material.encapsulationType'), placeholder: t('material.encapsulationTypeP'), component: 'el-input', type: 'text', model: null },
      {
        prop: 'materialCategory',
        label: t('material.materialCategory'),
        placeholder: t('material.materialCategoryP'),
        component: 'el-select',
        model: [],
        multiple: true,
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'rawMaterialNumber', label: t('material.rawMaterialNumber'), placeholder: t('material.rawMaterialNumberP'), component: 'el-input', type: 'text', model: null },
      { prop: 'texture', label: t('material.texture'), placeholder: t('material.textureP'), component: 'el-input', type: 'text', model: null },
      { prop: 'prePrinting', label: t('material.prePrinting'), placeholder: t('material.prePrintingP'), component: 'el-select', type: 'number', model: null, options: prePrintingOption },
      { prop: 'specialSize', label: t('material.specialSize'), placeholder: t('material.specialSizeP'), component: 'el-input', type: 'text', model: null },
      { prop: 'capacityUnit', label: t('material.capacityUnit'), placeholder: t('material.capacityUnitP'), component: 'el-input', type: 'text', model: null },
      { prop: 'layoutColumns', label: t('material.layoutColumns'), placeholder: t('material.layoutColumnsP'), component: 'el-input', type: 'number', model: null },
      { prop: 'barcodeRibbon', label: t('material.barcodeRibbon'), placeholder: t('material.barcodeRibbonP'), component: 'el-input', type: 'text', model: null }
    ]

    const updateStruct = [
      { prop: 'materialModel', label: t('material.materialModel'), placeholder: t('material.materialModelP'), component: 'el-input', type: 'text', model: null },
      { prop: 'materialName', label: t('material.materialName'), placeholder: t('material.materialNameP'), component: 'el-input', type: 'text', model: null },
      {
        prop: 'materialType',
        label: t('material.materialType'),
        placeholder: t('material.materialTypeP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'labelWidth', label: t('material.labelWidth'), placeholder: t('material.labelWidthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'labelLength', label: t('material.labelLength'), placeholder: t('material.labelLengthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'usableWidth', label: t('material.usableWidth'), placeholder: t('material.usableWidthP'), component: 'el-input', type: 'number', model: null },
      { prop: 'usableLength', label: t('material.usableLength'), placeholder: t('material.usableLengthP'), component: 'el-input', type: 'number', model: null },
      {
        prop: 'ribbonColor',
        label: t('material.ribbonColor'),
        placeholder: t('material.ribbonColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperColor',
        label: t('material.paperColor'),
        placeholder: t('material.paperColorP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      {
        prop: 'paperTexture',
        label: t('material.paperTexture'),
        placeholder: t('material.paperTextureP'),
        component: 'el-select',
        model: '',
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      // { prop: 'labelPreviewImage', label: t('material.labelPreviewImage'), placeholder: t('material.labelPreviewImageP'), component: 'el-input', type: 'text', model: null },
      { prop: 'labelCapacity', label: t('material.labelCapacity'), placeholder: t('material.labelCapacityP'), component: 'el-input', type: 'number', model: null },
      { prop: 'encapsulationType', label: t('material.encapsulationType'), placeholder: t('material.encapsulationTypeP'), component: 'el-input', type: 'text', model: null },
      {
        prop: 'materialCategory',
        label: t('material.materialCategory'),
        placeholder: t('material.materialCategoryP'),
        component: 'el-select',
        model: [],
        multiple: true,
        filterable: true,
        remote: true,
        loading: false,
        remoteMethod: async(query: string) => { console.log(query) },
        options: [] as any
      },
      { prop: 'rawMaterialNumber', label: t('material.rawMaterialNumber'), placeholder: t('material.rawMaterialNumberP'), component: 'el-input', type: 'text', model: null },
      { prop: 'texture', label: t('material.texture'), placeholder: t('material.textureP'), component: 'el-input', type: 'text', model: null },
      { prop: 'prePrinting', label: t('material.prePrinting'), placeholder: t('material.prePrintingP'), component: 'el-select', type: 'number', model: null, options: prePrintingOption },
      { prop: 'specialSize', label: t('material.specialSize'), placeholder: t('material.specialSizeP'), component: 'el-input', type: 'text', model: null },
      { prop: 'capacityUnit', label: t('material.capacityUnit'), placeholder: t('material.capacityUnitP'), component: 'el-input', type: 'text', model: null },
      { prop: 'layoutColumns', label: t('material.layoutColumns'), placeholder: t('material.layoutColumnsP'), component: 'el-input', type: 'number', model: null },
      { prop: 'barcodeRibbon', label: t('material.barcodeRibbon'), placeholder: t('material.barcodeRibbonP'), component: 'el-input', type: 'text', model: null }
    ]

    const materialRole = {
      materialModel: [{ required: true, message: t('material.materialModel').concat(t('common.isRequired')), trigger: 'blur' }],
      materialName: [{ required: true, message: t('material.materialName').concat(t('common.isRequired')), trigger: 'blur' }],
      materialType: [{ required: true, message: t('material.materialType').concat(t('common.isRequired')), trigger: 'blur' }],
      labelWidth: [{ required: true, message: t('material.labelWidth').concat(t('common.isRequired')), trigger: 'blur' }],
      usableWidth: [{ required: true, message: t('material.usableWidth').concat(t('common.isRequired')), trigger: 'blur' }],
      ribbonColor: [{ required: true, message: t('material.ribbonColor').concat(t('common.isRequired')), trigger: 'blur' }],
      paperColor: [{ required: true, message: t('material.paperColor').concat(t('common.isRequired')), trigger: 'blur' }],
      paperTexture: [{ required: true, message: t('material.paperTexture').concat(t('common.isRequired')), trigger: 'blur' }],
      labelPreviewImage: [{ required: true, message: t('material.labelPreviewImage').concat(t('common.isRequired')), trigger: 'blur' }],
      labelCapacity: [{ required: true, message: t('material.labelCapacity').concat(t('common.isRequired')), trigger: 'blur' }],
      encapsulationType: [{ required: true, message: t('material.encapsulationType').concat(t('common.isRequired')), trigger: 'blur' }],
      materialCategory: [{ required: true, message: t('material.materialCategory').concat(t('common.isRequired')), trigger: 'blur' }],
      labelBackgroundImage: [{ required: true, message: t('material.labelBackgroundImage').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [
      { prop: 'materialModel', label: t('material.materialModel'), sortable: 'custom' },
      { prop: 'materialName', label: t('material.materialName'), sortable: 'custom' },
      { prop: 'materialType', label: t('material.materialType'), formatter: (row: MaterialModel) => { return materialTypes.find(mt => mt.value === row.materialType.toString())?.label }, sortable: 'custom' },
      { prop: 'labelWidth', label: t('material.labelWidth'), sortable: 'custom' },
      { prop: 'labelLength', label: t('material.labelLength'), sortable: 'custom' },
      { prop: 'ribbonColor', label: t('material.ribbonColor'), formatter: (row: MaterialModel) => { return ribbonColors.find(mt => mt.value === row.ribbonColor)?.label }, sortable: 'custom' },
      { prop: 'paperColor', label: t('material.paperColor'), formatter: (row: MaterialModel) => { return paperColors.find(mt => mt.value === row.paperColor)?.label }, sortable: 'custom' },
      { prop: 'paperTexture', label: t('material.paperTexture'), formatter: (row: MaterialModel) => { return paperTextures.find(mt => mt.value === row.paperTexture)?.label }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as MaterialSearchModel,
      tableData: Array<MaterialModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'material.edit',
        create: 'material.create',
        tags: 'common.tags',
        examine: 'common.examine'
      },
      examineModel: Array<any>(),
      dialogFormVisible: false,
      materialRole: materialRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as MaterialAddViewModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as MaterialUpdateViewModel,
      materialTags: Array<MaterialTagsModel>(),
      materialTagsValue: null,
      materialId: 0,
      allTags: Array<any>(),
      loadingTags: false,
      searchTagVisible: false,
      editRow: {} as MaterialModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: MaterialPageSearchModel = {
          materialModel: dataMap.searchFormModel.materialModel,
          materialName: dataMap.searchFormModel.materialName,
          materialType: dataMap.searchFormModel.materialType,
          labelWidth: dataMap.searchFormModel.labelWidth,
          labelLength: dataMap.searchFormModel.labelLength,
          usableWidth: dataMap.searchFormModel.usableWidth,
          usableLength: dataMap.searchFormModel.usableLength,
          ribbonColor: dataMap.searchFormModel.ribbonColor,
          paperColor: dataMap.searchFormModel.paperColor,
          paperTexture: dataMap.searchFormModel.paperTexture,
          labelPreviewImage: dataMap.searchFormModel.labelPreviewImage,
          labelCapacity: dataMap.searchFormModel.labelCapacity,
          encapsulationType: dataMap.searchFormModel.encapsulationType,
          materialCategory: dataMap.searchFormModel.materialCategory,
          labelBackgroundImage: dataMap.searchFormModel.labelBackgroundImage,
          rawMaterialNumber: dataMap.searchFormModel.rawMaterialNumber,
          texture: dataMap.searchFormModel.texture,
          prePrinting: dataMap.searchFormModel.prePrinting,
          specialSize: dataMap.searchFormModel.specialSize,
          capacityUnit: dataMap.searchFormModel.capacityUnit,
          layoutColumns: dataMap.searchFormModel.layoutColumns,
          barcodeRibbon: dataMap.searchFormModel.barcodeRibbon,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchMaterial(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      uploadReset() {
        console.log('upload reset')
        if (createUpload.value) {
          createUpload.value.onReset()
        }
        if (labelBackgroundImageRef.value) {
          labelBackgroundImageRef.value.onReset()
        }
        dataMap.createModel.labelPreviewImage = null
        dataMap.createModel.labelBackgroundImage = null
      },
      labelPreviewImageChange(file: any) {
        dataMap.createModel.labelPreviewImage = file
      },
      labelBackgroundImageChange(file: any) {
        dataMap.createModel.labelBackgroundImage = file
      },
      async createSubmit() {
        const createData: MaterialAddModel = {
          materialModel: dataMap.createModel.materialModel,
          materialName: dataMap.createModel.materialName,
          materialType: dataMap.createModel.materialType,
          labelWidth: dataMap.createModel.labelWidth,
          labelLength: dataMap.createModel.labelLength,
          usableWidth: dataMap.createModel.usableWidth,
          usableLength: dataMap.createModel.usableLength,
          ribbonColor: dataMap.createModel.ribbonColor,
          paperColor: dataMap.createModel.paperColor,
          paperTexture: dataMap.createModel.paperTexture,
          labelPreviewImage: dataMap.createModel.labelPreviewImage,
          labelCapacity: dataMap.createModel.labelCapacity,
          encapsulationType: dataMap.createModel.encapsulationType,
          materialCategory: dataMap.createModel.materialCategory?.join(',') ?? null,
          labelBackgroundImage: dataMap.createModel.labelBackgroundImage,
          rawMaterialNumber: dataMap.createModel.rawMaterialNumber,
          texture: dataMap.createModel.texture,
          prePrinting: dataMap.createModel.prePrinting,
          specialSize: dataMap.createModel.specialSize,
          capacityUnit: dataMap.createModel.capacityUnit,
          layoutColumns: dataMap.createModel.layoutColumns,
          barcodeRibbon: dataMap.createModel.barcodeRibbon
        }
        const params = new FormData()
        params.append('data', JSON.stringify(createData))
        params.append('materialThumbnail', dataMap.createModel.labelPreviewImage.raw)
        params.append('labelBackgroundImage', dataMap.createModel.labelBackgroundImage.raw)
        const result = await addMaterial(params)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.status) {
          if (result.data.code === 0) {
            ElMessage({
              message: t('common.createSuccess'),
              type: 'success',
              duration: 2000
            })
          } else {
            ElMessage({
              message: result?.data.message,
              type: 'error',
              duration: 2000
            })
          }
        } else {
          ElMessage({
            message: result?.statusText,
            type: 'error',
            duration: 2000
          })
        }
        /*
        const createData: MaterialAddModel = {
          materialModel: dataMap.createModel.materialModel,
          materialName: dataMap.createModel.materialName,
          materialType: dataMap.createModel.materialType,
          labelWidth: dataMap.createModel.labelWidth,
          labelLength: dataMap.createModel.labelLength,
          usableWidth: dataMap.createModel.usableWidth,
          usableLength: dataMap.createModel.usableLength,
          ribbonColor: dataMap.createModel.ribbonColor,
          paperColor: dataMap.createModel.paperColor,
          paperTexture: dataMap.createModel.paperTexture,
          labelPreviewImage: dataMap.createModel.labelPreviewImage,
          labelCapacity: dataMap.createModel.labelCapacity,
          encapsulationType: dataMap.createModel.encapsulationType,
          materialCategory: dataMap.createModel.materialCategory?.join(',') ?? null,
          labelBackgroundImage: dataMap.createModel.labelBackgroundImage
        }
        const result = await addMaterial(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        } */
      },
      updateUploadReset() {
        console.log('upload reset')
        if (updateLabelPreviewImageRef.value) {
          updateLabelPreviewImageRef.value.onReset()
        }
        if (updateLabelBackgroundImageRef.value) {
          updateLabelBackgroundImageRef.value.onReset()
        }
        dataMap.updateModel.labelPreviewImageUpdate = null
        dataMap.updateModel.labelBackgroundImageUpdate = null
      },
      updateLabelPreviewImageChange(file: any) {
        dataMap.updateModel.labelPreviewImageUpdate = file
      },
      updateLabelBackgroundImageChange(file: any) {
        dataMap.updateModel.labelBackgroundImageUpdate = file
      },
      async updateNameSubmit() {
        const updateData: MaterialUpdateModel = {
          id: dataMap.updateModel.id,
          materialModel: dataMap.updateModel.materialModel,
          materialName: dataMap.updateModel.materialName,
          materialType: dataMap.updateModel.materialType,
          labelWidth: dataMap.updateModel.labelWidth,
          labelLength: dataMap.updateModel.labelLength,
          usableWidth: dataMap.updateModel.usableWidth,
          usableLength: dataMap.updateModel.usableLength,
          ribbonColor: dataMap.updateModel.ribbonColor,
          paperColor: dataMap.updateModel.paperColor,
          paperTexture: dataMap.updateModel.paperTexture,
          labelPreviewImage: dataMap.updateModel.labelPreviewImage,
          labelCapacity: dataMap.updateModel.labelCapacity,
          encapsulationType: dataMap.updateModel.encapsulationType,
          materialCategory: dataMap.updateModel.materialCategory?.join(',') ?? null,
          labelBackgroundImage: dataMap.updateModel.labelBackgroundImage,
          rawMaterialNumber: dataMap.updateModel.rawMaterialNumber,
          texture: dataMap.updateModel.texture,
          prePrinting: dataMap.updateModel.prePrinting,
          specialSize: dataMap.updateModel.specialSize,
          capacityUnit: dataMap.updateModel.capacityUnit,
          layoutColumns: dataMap.updateModel.layoutColumns,
          barcodeRibbon: dataMap.updateModel.barcodeRibbon
        }
        const params = new FormData()
        params.append('data', JSON.stringify(updateData))
        params.append('materialThumbnail', dataMap.updateModel.labelPreviewImageUpdate?.raw)
        params.append('labelBackgroundImage', dataMap.updateModel.labelBackgroundImageUpdate?.raw)
        const result = await updateMaterial(params)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.status) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.statusText,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeMaterial(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveMaterial(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.materialModel = dataMap.editRow.materialModel
        dataMap.updateModel.materialName = dataMap.editRow.materialName
        dataMap.updateModel.materialType = dataMap.editRow.materialType.toString()
        dataMap.updateModel.labelWidth = dataMap.editRow.labelWidth
        dataMap.updateModel.labelLength = dataMap.editRow.labelLength
        dataMap.updateModel.usableWidth = dataMap.editRow.usableWidth
        dataMap.updateModel.usableLength = dataMap.editRow.usableLength
        dataMap.updateModel.ribbonColor = dataMap.editRow.ribbonColor
        dataMap.updateModel.paperColor = dataMap.editRow.paperColor
        dataMap.updateModel.paperTexture = dataMap.editRow.paperTexture
        dataMap.updateModel.labelPreviewImage = dataMap.editRow.labelPreviewImage
        dataMap.updateModel.labelCapacity = dataMap.editRow.labelCapacity
        dataMap.updateModel.encapsulationType = dataMap.editRow.encapsulationType
        dataMap.updateModel.materialCategory = dataMap.editRow.materialCategory.split(',')
        dataMap.updateModel.labelBackgroundImage = dataMap.editRow.labelBackgroundImage
        dataMap.updateModel.rawMaterialNumber = dataMap.editRow.rawMaterialNumber
        dataMap.updateModel.texture = dataMap.editRow.texture
        dataMap.updateModel.prePrinting = dataMap.editRow.prePrinting
        dataMap.updateModel.specialSize = dataMap.editRow.specialSize
        dataMap.updateModel.capacityUnit = dataMap.editRow.capacityUnit
        dataMap.updateModel.layoutColumns = dataMap.editRow.layoutColumns
        dataMap.updateModel.barcodeRibbon = dataMap.editRow.barcodeRibbon
        dataMap.updateUploadReset()
        console.log('update reset')
      },
      createReset() {
        console.log('create reset')
        dataMap.uploadReset()
        if (createUpload.value) {
          createUpload.value.onReset()
        }
      },
      async deleteTag(id: number) {
        const result = await removeMaterialTags(id)
        dataMap.getMaterialTags()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async addTag(id: number) {
        const param: AddTagsModel = { materialId: dataMap.materialId, tagId: id }
        const result = await addMaterialTags(param)
        dataMap.getMaterialTags()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.addSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async getMaterialTags() {
        const result = await getMaterialTags(dataMap.materialId)
        dataMap.materialTags = result?.data ?? []
        dataMap.getAllTags('')
      },
      async getAllTags(value: string) {
        dataMap.loadingTags = true
        const param: TagsSearchModel = { tagValue: value }
        const result = await searchTags(param)
        dataMap.allTags = result?.data.filter(d => !dataMap.materialTags.some(t => t.tag === d.tagValue)).map(d => ({ value: d.id, label: d.tagValue })) ?? []
        dataMap.loadingTags = false
      }
    })
    const getPaperColorOptions = async(query: string) => {
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[8].loading = true
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[8].loading = true
        }
      } else {
        dataMap.searchFormStruct[6].loading = true
      }
      const params: PaperColorDefSearchModel = {
        colorComment: query,
        colorValueB: null,
        colorValueG: null,
        colorValueR: null
      }
      const data = await searchPaperColorDef(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.colorComment, value: iterator.id })
      }
      paperColors = optionList
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[8].options = optionList
          dataMap.createStruct[8].loading = false
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[8].options = optionList
          dataMap.updateStruct[8].loading = false
        }
      } else {
        dataMap.searchFormStruct[6].options = optionList
        dataMap.searchFormStruct[6].loading = false
      }
    }
    const getPaperTextureOptions = async(query: string) => {
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[9].loading = true
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[9].loading = true
        }
      } else {
        dataMap.searchFormStruct[7].loading = true
      }
      const params: PaperTextureDefSearchModel = {
        textureComment: query
      }
      const data = await searchPaperTextureDef(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.textureComment, value: iterator.id })
      }
      paperTextures = optionList
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[9].options = optionList
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[9].options = optionList
        }
      } else {
        dataMap.searchFormStruct[7].options = optionList
      }
      dataMap.createStruct[9].loading = false
      dataMap.updateStruct[9].loading = false
      dataMap.searchFormStruct[7].loading = false
    }
    const getRibbonColorOptions = async(query: string) => {
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[7].loading = true
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[7].loading = true
        }
      } else {
        dataMap.searchFormStruct[5].loading = true
      }
      const params: RibbonColorDefSearchModel = {
        colorComment: query
      }
      const data = await searchRibbonColorDef(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.colorComment, value: iterator.id })
      }
      ribbonColors = optionList
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[7].options = optionList
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[7].options = optionList
        }
      } else {
        dataMap.searchFormStruct[5].options = optionList
      }
      dataMap.createStruct[7].loading = false
      dataMap.updateStruct[7].loading = false
      dataMap.searchFormStruct[5].loading = false
    }
    const getMaterialCategoryOptions = async(query: string) => {
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[13].loading = true
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[13].loading = true
        }
      } else {
        dataMap.searchFormStruct[8].loading = true
      }
      const params: MaterialCategorySearchModel = {
        categoryName: query,
        fatherCategoryId: null
      }
      const data = await searchMaterialCategory(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.categoryName, value: iterator.id })
      }
      materialCategorys = optionList
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[12].options = optionList
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[12].options = optionList
        }
      } else {
        dataMap.searchFormStruct[8].options = optionList
      }
      dataMap.createStruct[12].loading = false
      dataMap.updateStruct[12].loading = false
      dataMap.searchFormStruct[8].loading = false
    }
    const getMaterialTypeOptions = async(query: string) => {
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[2].loading = true
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[2].loading = true
        }
      } else {
        dataMap.searchFormStruct[2].loading = true
      }
      const params: MaterialTypeSearchModel = {
        name: query,
        baseType: null,
        aUsable: null,
        bUsable: null,
        cUsable: null,
        dUsable: null,
        eUsable: null,
        fUsable: null,
        labelSizeImage: null
      }
      const data = await searchMaterialType(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({ label: iterator.name, value: iterator.id })
      }
      materialTypes = optionList
      if (dataMap.dialogFormVisible) {
        if (dataMap.dialogStatus === 'create') {
          dataMap.createStruct[2].options = optionList
        } else if (dataMap.dialogStatus === 'update') {
          dataMap.updateStruct[2].options = optionList
        }
      } else {
        dataMap.searchFormStruct[2].options = optionList
      }
      dataMap.createStruct[2].loading = false
      dataMap.updateStruct[2].loading = false
      dataMap.searchFormStruct[2].loading = false
    }
    const handleExamine = (row?: MaterialModel) => {
      if (row) {
        dataMap.dialogStatus = 'examine'
        const examineData: any[] = []
        examineData.push({ label: 'material.materialModel', value: row.materialModel })
        examineData.push({ label: 'material.materialName', value: row.materialName })
        examineData.push({ label: 'material.materialType', value: materialTypes.find(mt => mt.value === row.materialType.toString())?.label })
        examineData.push({ label: 'material.labelWidth', value: row.labelWidth })
        examineData.push({ label: 'material.labelLength', value: row.labelLength })
        examineData.push({ label: 'material.usableWidth', value: row.usableWidth })
        examineData.push({ label: 'material.usableLength', value: row.usableLength })
        examineData.push({ label: 'material.ribbonColor', value: ribbonColors.find(mt => mt.value === row.ribbonColor)?.label })
        examineData.push({ label: 'material.paperColor', value: paperColors.find(mt => mt.value === row.paperColor)?.label })
        examineData.push({ label: 'material.paperTexture', value: paperTextures.find(mt => mt.value === row.paperTexture)?.label })
        examineData.push({ label: 'material.labelPreviewImage', value: row.labelPreviewImage, img: true })
        examineData.push({ label: 'material.labelCapacity', value: row.labelCapacity })
        examineData.push({ label: 'material.encapsulationType', value: row.encapsulationType })
        examineData.push({ label: 'material.materialCategory', value: row.materialCategory.split(',').map(mc => materialCategorys.find(mt => mt.value === mc)?.label).join(', ') })
        examineData.push({ label: 'material.labelBackgroundImage', value: row.labelBackgroundImage, img: true })
        examineData.push({ label: 'material.rawMaterialNumber', value: row.rawMaterialNumber })
        examineData.push({ label: 'material.texture', value: row.texture })
        examineData.push({ label: 'material.prePrinting', value: row.prePrinting })
        examineData.push({ label: 'material.specialSize', value: row.specialSize })
        examineData.push({ label: 'material.capacityUnit', value: row.capacityUnit })
        examineData.push({ label: 'material.layoutColumns', value: row.layoutColumns })
        examineData.push({ label: 'material.barcodeRibbon', value: row.barcodeRibbon })
        dataMap.examineModel = examineData
        dataMap.dialogFormVisible = true
      }
    }
    const handleCreate = () => {
      dataMap.uploadReset()
      dataMap.dialogStatus = 'create'
      getRibbonColorOptions('')
      getPaperColorOptions('')
      getPaperTextureOptions('')
      getMaterialCategoryOptions('')
      getMaterialTypeOptions('')
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: MaterialModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.materialModel = row.materialModel
        dataMap.updateModel.materialName = row.materialName
        dataMap.updateModel.materialType = row.materialType.toString()
        dataMap.updateModel.labelWidth = row.labelWidth
        dataMap.updateModel.labelLength = row.labelLength
        dataMap.updateModel.usableWidth = row.usableWidth
        dataMap.updateModel.usableLength = row.usableLength
        dataMap.updateModel.ribbonColor = row.ribbonColor
        dataMap.updateModel.paperColor = row.paperColor
        dataMap.updateModel.paperTexture = row.paperTexture
        dataMap.updateModel.labelPreviewImage = row.labelPreviewImage
        dataMap.updateModel.labelCapacity = row.labelCapacity
        dataMap.updateModel.encapsulationType = row.encapsulationType
        dataMap.updateModel.materialCategory = row.materialCategory.split(',')
        dataMap.updateModel.labelBackgroundImage = row.labelBackgroundImage
        dataMap.updateModel.rawMaterialNumber = row.rawMaterialNumber
        dataMap.updateModel.texture = row.texture
        dataMap.updateModel.prePrinting = row.prePrinting
        dataMap.updateModel.specialSize = row.specialSize
        dataMap.updateModel.capacityUnit = row.capacityUnit
        dataMap.updateModel.layoutColumns = row.layoutColumns
        dataMap.updateModel.barcodeRibbon = row.barcodeRibbon
        getRibbonColorOptions('')
        getPaperColorOptions('')
        getPaperTextureOptions('')
        getMaterialCategoryOptions('')
        getMaterialTypeOptions('')
        dataMap.dialogFormVisible = true
      }
    }
    const handleTags = async(id: number) => {
      dataMap.searchTagVisible = false
      dataMap.dialogStatus = 'tags'
      dataMap.materialId = id
      await dataMap.getMaterialTags()
      dataMap.dialogFormVisible = true
    }
    const showSearchTag = () => {
      dataMap.searchTagVisible = true
    }
    const selectTag = async(data: any) => {
      console.log(data)
      if (dataMap.allTags.some(t => t.value === data)) {
        dataMap.addTag(data)
      } else {
        const param: TagsAddModel = { tagValue: data }
        const result = await addTags(param)
        if (result?.code === 0) {
          const entity = await getTagsByValue(data)
          if (entity) {
            dataMap.addTag(entity.data.id)
          }
        }
      }
      dataMap.searchTagVisible = false
      dataMap.materialTagsValue = null
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
      dataMap.searchFormStruct[5].remoteMethod = getRibbonColorOptions
      dataMap.createStruct[7].remoteMethod = getRibbonColorOptions
      dataMap.updateStruct[7].remoteMethod = getRibbonColorOptions
      dataMap.searchFormStruct[6].remoteMethod = getPaperColorOptions
      dataMap.createStruct[8].remoteMethod = getPaperColorOptions
      dataMap.updateStruct[8].remoteMethod = getPaperColorOptions
      dataMap.searchFormStruct[7].remoteMethod = getPaperTextureOptions
      dataMap.createStruct[9].remoteMethod = getPaperTextureOptions
      dataMap.updateStruct[9].remoteMethod = getPaperTextureOptions
      dataMap.searchFormStruct[8].remoteMethod = getMaterialCategoryOptions
      dataMap.createStruct[12].remoteMethod = getMaterialCategoryOptions
      dataMap.updateStruct[12].remoteMethod = getMaterialCategoryOptions
      dataMap.searchFormStruct[2].remoteMethod = getMaterialTypeOptions
      dataMap.createStruct[2].remoteMethod = getMaterialTypeOptions
      dataMap.updateStruct[2].remoteMethod = getMaterialTypeOptions
      getRibbonColorOptions('')
      getPaperColorOptions('')
      getPaperTextureOptions('')
      getMaterialCategoryOptions('')
      getMaterialTypeOptions('')
    })
    return {
      t,
      handleExamine,
      handleCreate,
      handleEdit,
      handleTags,
      handleDelete,
      handleBathDelete,
      showSearchTag,
      selectTag,
      searchFormRef,
      createUpload,
      ...toRefs(dataMap)
    }
  }
})
</script>
